const installed = new WeakMap<Document, () => void>();

function horizontalScroller(target: EventTarget | null): HTMLElement | null {
  let node = target instanceof Element ? target : null;
  while (node && node !== document.documentElement) {
    const element = node as HTMLElement;
    const overflow = getComputedStyle(element).overflowX;
    if (/(auto|scroll|overlay)/.test(overflow) && element.scrollWidth > element.clientWidth + 1) return element;
    node = node.parentElement;
  }
  return null;
}

/** Both bridges share one guard. Native horizontal scrollers must own their swipes. */
export function installHostHorizontalSwipeGuard(doc = document): () => void {
  const existing = installed.get(doc);
  if (existing) return existing;
  let start: { x: number; y: number; scroller: HTMLElement | null; horizontal: boolean | null } | null = null;
  const reset = () => { start = null; };
  const down = (event: TouchEvent) => {
    reset();
    if (event.touches.length !== 1) return;
    start = { x: event.touches[0].clientX, y: event.touches[0].clientY, scroller: horizontalScroller(event.target), horizontal: null };
  };
  const move = (event: TouchEvent) => {
    if (event.touches.length !== 1) { reset(); return; }
    if (!start) return;
    const dx = event.touches[0].clientX - start.x;
    const dy = event.touches[0].clientY - start.y;
    if (start.horizontal === null && Math.max(Math.abs(dx), Math.abs(dy)) > 10) start.horizontal = Math.abs(dx) > Math.abs(dy);
    // Also recheck after layout changes (opening a dialog or rotating the phone).
    // Keep the entire gesture native, including reverse swipes at either edge.
    if (start.scroller || horizontalScroller(event.target)) return;
    if (start.horizontal && event.cancelable) event.preventDefault();
  };
  doc.addEventListener('touchstart', down, { passive: true });
  doc.addEventListener('touchmove', move, { passive: false });
  doc.addEventListener('touchend', reset, { passive: true });
  doc.addEventListener('touchcancel', reset, { passive: true });
  doc.addEventListener('visibilitychange', reset);
  doc.defaultView?.addEventListener('blur', reset);
  const detach = () => {
    reset();
    doc.removeEventListener('touchstart', down);
    doc.removeEventListener('touchmove', move);
    doc.removeEventListener('touchend', reset);
    doc.removeEventListener('touchcancel', reset);
    doc.removeEventListener('visibilitychange', reset);
    doc.defaultView?.removeEventListener('blur', reset);
    installed.delete(doc);
  };
  installed.set(doc, detach);
  return detach;
}
